import { Link, useRouterState } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Palmtree,
  Stethoscope,
  FileText,
  Briefcase,
  Gift,
  Calendar,
  Bell,
  FileBarChart,
  Settings,
  TrendingUp,
  DollarSign,
  ClipboardList,
  Layers,
  BookOpen,
  FileCode2,
  ScrollText,
} from 'lucide-react'

// `as const` mantém os hrefs como literais, para o `<Link to>` do TanStack
// Router validar cada rota em tempo de compilação.
const navItems = [
  {
    label: 'Principal',
    items: [
      { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Pessoas',
    items: [
      { href: '/colaboradores',  label: 'Colaboradores',  icon: Users },
      { href: '/estagiarios',    label: 'Estagiários',    icon: GraduationCap },
      { href: '/pre-cadastros',  label: 'Pré-cadastros',  icon: ClipboardList },
    ],
  },
  {
    label: 'Gestão',
    items: [
      { href: '/ferias',                label: 'Férias',                  icon: Palmtree },
      { href: '/exames',                label: 'Exames',                  icon: Stethoscope },
      { href: '/contratos',             label: 'Contratos',               icon: FileText },
      { href: '/contratos/templates',   label: 'Templates de Contrato',   icon: Layers },
      { href: '/cargos',                label: 'Cargos e Salários',       icon: Briefcase },
      { href: '/historico-salarial',    label: 'Histórico Salarial',      icon: TrendingUp },
      { href: '/beneficios',            label: 'Benefícios',              icon: Gift },
      { href: '/bonus-comissoes',       label: 'Bônus e Comissões',       icon: DollarSign },
    ],
  },
  {
    label: 'Organização',
    items: [
      { href: '/calendario',    label: 'Calendário',    icon: Calendar },
      { href: '/notificacoes',  label: 'Notificações',  icon: Bell },
      { href: '/relatorios',    label: 'Relatórios',    icon: FileBarChart },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { href: '/configuracoes',  label: 'Configurações',    icon: Settings },
      { href: '/logs',           label: 'Logs de Auditoria', icon: ScrollText },
      { href: '/manual',         label: 'Manual do Usuário', icon: BookOpen },
      { href: '/documentacao',   label: 'Documentação',      icon: FileCode2 },
    ],
  },
] as const

interface SidebarProps {
  systemName?: string
}

export function Sidebar({ systemName = 'Módulo RH' }: SidebarProps) {
  const pathname = useRouterState({ select: s => s.location.pathname })

  return (
    <aside
      className="w-64 flex flex-col h-full"
      style={{ background: '#FFFCF6', borderRight: '1px solid #ECE8DC' }}
    >
      {/* Brand / Logo */}
      <div
        className="h-16 flex items-center gap-3 px-5 flex-shrink-0"
        style={{ borderBottom: '1px solid #ECE8DC' }}
      >
        <img
          src="/sw-logo-full.webp"
          alt="Sprache &amp; Wissen"
          style={{ height: 20, width: 'auto', display: 'block' }}
        />
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#6B6A64',
            borderLeft: '1px solid #ECE8DC',
            paddingLeft: 10,
            whiteSpace: 'nowrap',
          }}
        >
          {systemName}
        </span>
      </div>

      {/* Navegação */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {navItems.map((group) => (
          <div key={group.label} className="mb-5">
            <span className="sw-nav-group-label">{group.label}</span>

            {group.items.map((item) => {
              const Icon = item.icon
              const isActive =
                pathname === item.href ||
                (item.href !== '/dashboard' && pathname.startsWith(item.href))

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn('sw-nav-item', isActive && 'active')}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </Link>
              )
            })}
          </div>
        ))}
      </nav>
    </aside>
  )
}
